'use client';

import type { EditorView } from '@codemirror/view';
import { XMarkIcon } from '@heroicons/react/16/solid';
import type React from 'react';
import { useRef, useState } from 'react';
import { cn } from '../../../../../../../shared/cn';
import { LoadingSpinner } from '../../../../../../atoms/LoadingSpinner';
import { Caption } from '../../../../../../typography/Caption';
import { Label3 } from '../../../../../../typography/Label';
import { Toast, type ToastHandle } from './_components/FormatToast';
import { MoreDropdown } from './_components/ToolbarDropdown/MoreDropdown';
import { TextDropdown } from './_components/ToolbarDropdown/TextDropdown';
import { BUTTON_GROUP, getToolbarButtons } from './_utils/buttons';
import { getShortcutTooltip, isMacPlatform } from './_utils/markdownShortcuts';
type ToolbarProps = {
  editorView: EditorView | null;
  editorWidth: number;
  singleView?: boolean;
  small?: boolean;
  onClose?: () => void;
  onFormat?: () => Promise<void> | void;
  isEditable?: boolean;
};
export const EditorToolbar: React.FC<ToolbarProps> = ({
  editorView,
  editorWidth,
  singleView,
  small = false,
  onClose,
  onFormat,
  isEditable = true
}) => {
  const toastRef = useRef<ToastHandle>(null);
  const [formatting, setFormatting] = useState<boolean>(false);
  const handleFormatClick = async () => {
    if (!onFormat) return;
    setFormatting(true);
    try {
      await onFormat();
      toastRef.current?.show();
    } finally {
      setFormatting(false);
    }
  };
  const buttonConfigs = getToolbarButtons(editorView);
  const smallButtons = buttonConfigs.filter(btn => btn.group === BUTTON_GROUP.SMALL);
  const extendedButtons = buttonConfigs.filter(btn_0 => btn_0.group === BUTTON_GROUP.EXTENDED);
  const fullButtons = buttonConfigs.filter(btn_1 => btn_1.group === BUTTON_GROUP.FULL);
  let variant: 'collapsed' | 'default' | 'full' = 'default';
  if (editorWidth < 600) variant = 'collapsed';else if (singleView && editorWidth > 1010) variant = 'full';else if (!singleView) variant = editorWidth > 600 ? 'default' : 'collapsed';
  const visibleButtons = variant === 'collapsed' ? smallButtons : variant === 'full' ? [...smallButtons, ...extendedButtons, ...fullButtons] : [...smallButtons, ...extendedButtons];
  return <main className='relative'>
      <Toast ref={toastRef} />

      <div className='mx-auto flex w-fit items-center gap-0 rounded-md border border-borderPrimary bg-backgroundTertiary p-2 text-contentPrimary shadow-md md:gap-2'>
        <TextDropdown editorView={editorView} isEditable={isEditable} />

        {visibleButtons.map(btn_2 => {
        const isSectionFull = variant === 'full' && btn_2.group === BUTTON_GROUP.FULL;
        const isFormatButton = btn_2.label === 'Formatuj';
        const tooltip = btn_2.shortcut ? getShortcutTooltip(btn_2.shortcut, isMacPlatform()) : '';
        return <div key={btn_2.label} className='group relative'>
              <button type='button' onClick={isFormatButton ? handleFormatClick : btn_2.action} disabled={!isEditable || isFormatButton && formatting} className={cn('flex cursor-default items-center gap-1 rounded-md p-1.5 transition-colors hover:bg-backgroundHoverOverlay', !isEditable && 'pointer-events-none opacity-60', isFormatButton && 'border border-borderPrimary bg-backgroundPrimary px-2 py-1')}>
                {isFormatButton && formatting ? <LoadingSpinner size='button' className='border-contentPrimary border-t-transparent' /> : <btn_2.icon className='h-4 w-4 text-contentPrimary' />}
                {isSectionFull && <Label3 className='ml-1'>{btn_2.label}</Label3>}
              </button>

              <Caption className='absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded border border-borderPrimary bg-backgroundPrimary px-2 py-0.5 text-contentTertiary shadow-sm group-hover:block'>
                {btn_2.label} {tooltip}
              </Caption>
            </div>;
      })}

        {variant !== 'full' && <MoreDropdown moreButtons={{
        textButtons: variant === 'collapsed' ? extendedButtons : [],
        elementButtons: fullButtons
      }} onFormat={handleFormatClick} isEditable={isEditable} />}

        {small && onClose && <div className='border-borderPrimary border-l pl-1'>
            <button type='button' onClick={onClose} className='flex cursor-default items-center gap-1 rounded-md p-1.5 transition-colors hover:bg-backgroundHoverOverlay'>
              <XMarkIcon className='h-4 w-4' />
            </button>
          </div>}
      </div>
    </main>;
};